<script setup lang="ts">
import type { PartnerPortalContactSessionFragmentFragment } from '@/graphql/pp/generated/graphql';
import {
  matchesPathOrDescendant,
  sidebarNavigationMenuUi,
  toNavigationMenuItems,
  type SidebarNavItemInput,
} from '@/util/navigationMenu';

const props = defineProps<{ partner: PartnerPortalContactSessionFragmentFragment }>();
const route = useRoute();

const partnerUuid = computed(() => props.partner.partner.uuid);
const partnerBase = computed(() => `/${partnerUuid.value}`);
const reportPath = computed(() => `${partnerBase.value}/reporting/report`);

const hasCoverageConfirmation = computed(() => {
  const school = props.partner.partner.school;
  return school.hasRentersCoverageConfirmationPro || school.hasRentersCoverageConfirmationLite;
});

const partnerPortal = computed<SidebarNavItemInput[]>(() => [
  {
    faIcon: ['fal', 'gauge-high'],
    label: 'Dashboard',
    to: partnerBase.value,
    value: partnerBase.value,
  },
  {
    faIcon: ['fal', 'chart-waterfall'],
    label: 'Reporting',
    value: 'reporting',
    children: [
      {
        faIcon: ['fal', 'chart-pie-simple'],
        label: 'Reports',
        to: reportPath.value,
        value: 'reporting/report',
        active: matchesPathOrDescendant(route.path, reportPath.value),
        'data-cy': 'menu-reporting/report',
      },
      {
        faIcon: ['fal', 'clipboard'],
        label: 'Acknowledgements',
        to: `${partnerBase.value}/reporting/acknowledgements`,
        value: 'reporting/acknowledgements',
        'data-cy': 'menu-reporting/acknowledgements',
      },
    ],
  },
]);

const students = computed<SidebarNavItemInput[]>(() => {
  const rentersChildren: SidebarNavItemInput[] = [
    {
      faIcon: ['fal', 'file-spreadsheet'],
      label: 'Renters Policies',
      to: `${partnerBase.value}/renters/roster`,
      value: 'renters/roster',
      'data-cy': 'menu-renters/roster',
    },
  ];
  if (hasCoverageConfirmation.value) {
    rentersChildren.push({
      faIcon: ['fal', 'circle-check'],
      label: 'Coverage Confirmation',
      to: `${partnerBase.value}/renters/coverage-confirmation`,
      value: 'renters/coverage-confirmation',
      'data-cy': 'menu-renters/coverage-confirmation',
    });
  }

  return [
    {
      faIcon: ['fal', 'user-graduate'],
      label: 'Tuition',
      value: 'tuition',
      children: [
        {
          faIcon: ['fal', 'file-spreadsheet'],
          label: 'Tuition Policies',
          to: `${partnerBase.value}/tuition/roster`,
          value: 'tuition/roster',
          'data-cy': 'menu-tuition/roster',
        },
        {
          faIcon: ['fal', 'list-check'],
          label: 'Refund Acknowledgment',
          to: `${partnerBase.value}/tuition/refund`,
          value: 'tuition/refund',
          'data-cy': 'menu-tuition/refund',
        },
      ],
    },
    {
      faIcon: ['fal', 'house'],
      label: 'Renters',
      value: 'renters',
      children: rentersChildren,
    }
  ];
});

const partnerPortalItems = computed(() => toNavigationMenuItems(
  partnerPortal.value,
  route.path,
));
const studentsItems = computed(() => toNavigationMenuItems(
  students.value,
  route.path,
));
</script>

<template>
  <LayoutSidebarMenuTitle title="Partner Portal" class="mb-3" />

  <StudentsSearchFormSidebarWrap
    :partner-uuid="partnerUuid"
    class="mb-4"
  >
    <template #default="{ onSubmit }">
      <StudentsSearchForm :on-submit="onSubmit" />
    </template>
  </StudentsSearchFormSidebarWrap>

  <nav aria-label="Partner Portal">
    <MenuSidebarNavigationMenu
      :items="partnerPortalItems"
      :ui="sidebarNavigationMenuUi"
    />
  </nav>

  <USeparator
    decorative
    class="my-4"
    :ui="{ border: 'border-(--color-sidebar-text)/25' }"
  />

  <LayoutSidebarMenuTitle title="Students" class="mb-3" />
  <nav aria-label="Students" data-cy="menu-students">
    <MenuSidebarNavigationMenu
      :items="studentsItems"
      :ui="sidebarNavigationMenuUi"
    />
  </nav>

  <LayoutSidebarSchoolMenuWaiver :partner="partner" />
</template>
